
import { ArrowLeftRight, Download, Search } from 'lucide-react';

import { libraryApi } from '../../api/endpoints';
import { Button, Pill } from '../../components/ui';
import { querySourceLabel, resolution, seconds, timeLabel } from './format';
import { MediaThumb } from './MediaThumb';
import type { PlacementItem, PlacementMedia } from './types';
import styles from './placement.module.css';

interface Props {
  item: PlacementItem | null;
  chosen: PlacementMedia | null;
  overridden: boolean;
  onChoose: (media: PlacementMedia) => void;
  onReset: () => void;
}

export function InsertDetails({ item, chosen, overridden, onChoose, onReset }: Props) {
  if (!item) {
    return (
      <div className={styles.side}>
        <div className={styles.panelHead}>
          <span className={styles.panelTitle}>Выбранная вставка</span>
        </div>
        <div className={styles.sideBody}>
          <span className={styles.sideText}>Нажмите вставку в ленте.</span>
        </div>
      </div>
    );
  }

  const alternatives = item.alternatives.filter((media) => media.itemId !== chosen?.itemId);
  const size = chosen ? resolution(chosen.width, chosen.height) : null;

  return (
    <div className={styles.side}>
      <div className={styles.panelHead}>
        <span className={styles.panelTitle}>Выбранная вставка</span>
        <span className={styles.panelCount} title={`${item.startTc} – ${item.endTc}`}>
          {timeLabel(item.startMs, item.timing)} – {timeLabel(item.endMs, item.timing)}
        </span>
      </div>

      <MediaThumb media={chosen} className={styles.sidePreview} controls size={26} />

      <div className={styles.sideBody}>
        <div className={styles.sideTitle}>{chosen ? chosen.title : 'Медиа не подобрано'}</div>

        <div className={styles.tagRow}>
          <Pill>{seconds(item.durationMs)} в монтаже</Pill>
          {chosen?.durationMs ? <Pill>файл {seconds(chosen.durationMs)}</Pill> : null}
          {size && <Pill>{size}</Pill>}
          {chosen?.source && <Pill>{chosen.source}</Pill>}
          {chosen?.rank !== null && chosen?.rank !== undefined && (
            <Pill>№{chosen.rank} в выдаче</Pill>
          )}
        </div>

        <div className={styles.quote}>«{item.anchorText}»</div>
        <span className={styles.sideText}>
          слово №{item.wordNumber}, абзац {item.segmentIndex + 1}
        </span>

        {chosen && (
          <>
            <div className={styles.sideText}>
              <Search size={12} strokeWidth={1.6} /> нашлось по запросу «{chosen.query}» (
              {querySourceLabel(chosen.querySource)})
            </div>
            <div className={styles.sideText}>{chosen.why}</div>
          </>
        )}

        {item.weak && !overridden && (
          <div className={`${styles.banner} ${styles.bannerWarn}`}>
            <span className={styles.bannerText}>
              Сомнительная вставка: {item.weakReason}. Проверьте вставку или замените её на
              запасной вариант.
            </span>
          </div>
        )}

        {chosen?.shorterThanWindow && (
          <div className={`${styles.banner} ${styles.bannerWarn}`}>
            <span className={styles.bannerText}>
              Файл короче окна показа: растяните его в монтаже или замените.
            </span>
          </div>
        )}

        {item.searchDegraded && (
          <div className={`${styles.banner} ${styles.bannerWarn}`}>
            <span className={styles.bannerText}>
              Поиск работал в упрощённом режиме: {item.searchDegradedReason || 'причина неизвестна'}.
            </span>
          </div>
        )}

        {item.notes.length > 0 && (
          <ul className={styles.sideText} style={{ margin: 0, paddingLeft: 16 }}>
            {item.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        )}
      </div>

      <div className={styles.sideSection}>
        чем заменить {alternatives.length > 0 ? `· ${alternatives.length}` : ''}
      </div>

      {alternatives.length === 0 ? (
        <div className={styles.sideBody}>
          <span className={styles.sideText}>
            Замен нет: подошёл только этот материал. Догрузите похожие в библиотеку.
          </span>
        </div>
      ) : (
        alternatives.map((media) => (
          <button
            key={media.itemId}
            type="button"
            className={styles.altRow}
            onClick={() => onChoose(media)}
            title={media.why}
          >
            <MediaThumb media={media} className={styles.altThumb} size={13} />
            <span className={styles.altMain}>
              <span className={styles.altName}>{media.title}</span>
              <span className={styles.altMeta}>
                {[seconds(media.durationMs), resolution(media.width, media.height), media.source]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>
            <Pill>№{media.rank ?? '–'}</Pill>
          </button>
        ))
      )}

      <div className={styles.sideActions}>
        {chosen?.url ? (
          <a
            className={`${styles.linkBtn} ${styles.linkBtnPrimary}`}
            href={libraryApi.downloadUrl(chosen.itemId)}
            style={{ flex: 1, justifyContent: 'center' }}
          >
            <Download size={13} strokeWidth={1.6} /> Скачать файл
          </a>
        ) : (
          <span className={`${styles.linkBtn} ${styles.linkBtnDisabled}`} style={{ flex: 1 }}>
            Ссылки на файл нет
          </span>
        )}
        {overridden && (
          <Button size="sm" onClick={onReset} title="Вернуть исходный подбор">
            <ArrowLeftRight size={13} strokeWidth={1.6} /> Вернуть исходный
          </Button>
        )}
      </div>
    </div>
  );
}
